"use client";

import * as React from "react";
import * as CheckboxPrimitive from "@radix-ui/react-checkbox";

import { VariantProps, tv } from "tailwind-variants";
import { cn } from "@/lib/utils";
import CheckIcon from "@/components/icons/check-icon";

const checkboxVariant = tv({
  slots: {
    root: [
      "group flex aspect-square shrink-0 cursor-pointer items-center justify-center rounded outline-none",
      "border-neutral-400 bg-white",
      "[&:not(:disabled)]:data-[state=checked]:border-secondary-600",
      "hover:border-secondary-500 hover:bg-secondary-200",
      "focus:border-secondary-600",
    ],
    indicator: [
      "flex h-full w-full items-center justify-center text-white",
      "rounded-sm [&:not(:disabled)]:bg-secondary-600",
    ],
  },
  variants: {
    size: {
      lg: {
        root: "h-6 w-6 border-2",
      },
      sm: {
        root: "h-4 w-4 border",
      },
    },
    disabled: {
      true: {
        root: "cursor-not-allowed disabled:!border-neutral-300 disabled:!bg-neutral-200",
        indicator: "hidden",
      },
      false: {
        root: "",
        indicator: "",
      },
    },
    error: {
      true: {
        root: "border-sentiment-negative-300",
      },
      false: {
        root: "",
      },
    },
  },
  defaultVariants: {
    size: "lg",
  },
});

type CheckboxVariant = VariantProps<typeof checkboxVariant>;

export interface CheckboxProps
  extends Omit<
      React.ComponentPropsWithoutRef<typeof CheckboxPrimitive.Root>,
      "size" | "error"
    >,
    CheckboxVariant {}

const Checkbox = React.forwardRef<
  React.ElementRef<typeof CheckboxPrimitive.Root>,
  CheckboxProps
>(({ className, error, ...props }, ref) => {
  const { root, indicator } = checkboxVariant({
    size: props.size,
    disabled: props.disabled,
    error,
    className,
  });

  return (
    <CheckboxPrimitive.Root
      ref={ref}
      className={root({
        class: [
          error && props.size === "lg" && "[&:not(:disabled)]:border-[3px]",
          error && props.size === "sm" && "[&:not(:disabled)]:border-[2px]",
          className,
        ],
      })}
      aria-invalid={error || undefined}
      {...props}
    >
      <CheckboxPrimitive.Indicator className={indicator()}>
        <CheckIcon
          className={cn(props.size === "lg" ? "h-4 w-4" : "h-2 w-2")}
        />
      </CheckboxPrimitive.Indicator>
    </CheckboxPrimitive.Root>
  );
});
Checkbox.displayName = CheckboxPrimitive.Root.displayName;

export default Checkbox;
